<section class="manage-users-page">
  <div class="page-heading">
    <div>
      <p>User Administration</p>
      <h1>Manage Users</h1>
      <span>Add users, edit profile details, assign privileges, activate/deactivate accounts, and regenerate passwords.</span>
    </div>
    @if (!isFormOpen()) {
      <button type="button" (click)="openCreateForm()">Add New</button>
    }
  </div>

  @if (toast()) {
    <div class="toast-message" [class.error]="toast()?.type === 'error'" role="status">
      {{ toast()?.message }}
    </div>
  }

  @if (isFormOpen()) {
    <form class="config-card user-form" [formGroup]="form" (ngSubmit)="save()">
      <div class="card-heading">
        <h2>{{ editingUserId ? 'Edit User Details' : 'Add New User' }}</h2>
        <p>Backend generates the initial password automatically for new users.</p>
      </div>

      <div class="form-grid">
        <label>
          <span>Name <b>*</b></span>
          <input formControlName="name" placeholder="First name">
        </label>

        <label>
          <span>Lastname <b>*</b></span>
          <input formControlName="lastname" placeholder="Last name">
        </label>

        <label>
          <span>User Category <b>*</b></span>
          <select formControlName="user_category">
            <option value="PORTAL">Portal</option>
            <option value="ADMIN">Admin</option>
            <option value="APPROVER">Approver</option>
            <option value="VIEWER">Viewer</option>
          </select>
        </label>

        <label>
          <span>Email <b>*</b></span>
          <input type="email" formControlName="email" placeholder="name@example.com">
        </label>

        <label>
          <span>Mobile</span>
          <input formControlName="mobile" placeholder="Mobile number">
        </label>

        <label>
          <span>Profile Image</span>
          <input formControlName="profile_image" placeholder="Image URL or path">
        </label>

        <label>
          <span>Privileges <b>*</b></span>
          <select formControlName="role_id">
            <option [value]="0">Select role</option>
            @for (role of roles(); track role.id) {
              <option [value]="role.id">{{ role.name }}</option>
            }
          </select>
        </label>

        <label class="toggle-row">
          <input type="checkbox" formControlName="account_active">
          <span>Account Active</span>
        </label>
      </div>

      <div class="form-actions">
        <button type="submit">{{ editingUserId ? 'Update User' : 'Create User' }}</button>
        <button class="secondary-button" type="button" (click)="closeForm()">Cancel</button>
      </div>
    </form>
  } @else {
    <div class="config-card users-list">
      <div class="toolbar">
        <div class="card-heading">
          <h2>All Users</h2>
          <p>Search by email, name, or mobile number.</p>
        </div>
        <label class="search-box">
          <span>Search</span>
          <input type="search" placeholder="Search users" (input)="onSearch($any($event.target).value)">
        </label>
      </div>

      <div class="table-wrap">
        <table class="table-sm">
          <tr>
            <th>Sr.No</th>
            <th>Name</th>
            <th>Category</th>
            <th>Email</th>
            <th>Mobile</th>
            <th>Privileges</th>
            <th>Status</th>
            <th>Last Login</th>
            <th>Action</th>
          </tr>
          @for (user of paginatedUsers(); track user.id; let index = $index) {
            <tr>
              <td>{{ ((currentPage() - 1) * pageSize()) + index + 1 }}</td>
              <td>{{ user.name }} {{ user.lastname }}</td>
              <td>{{ user.user_category }}</td>
              <td>{{ user.email }}</td>
              <td>{{ user.mobile || '-' }}</td>
              <td>{{ user.role?.name || '-' }}</td>
              <td>
                <button
                  type="button"
                  class="status-toggle"
                  [class.inactive]="!user.account_active"
                  (click)="toggleAccount(user)"
                  [attr.aria-label]="user.account_active ? 'Deactivate account' : 'Activate account'"
                >
                  <span></span>
                </button>
              </td>
              <td>{{ formatDateTime(user.last_login) }}</td>
              <td class="action-cell">
                <button type="button" class="dots-button" (click)="toggleActions(user.id, $event)" aria-label="Open actions">⋮</button>
                @if (openActionUserId() === user.id) {
                  <div class="action-menu" [style.top.px]="actionMenuPosition().top" [style.left.px]="actionMenuPosition().left">
                    <button type="button" (click)="edit(user)"><span>✎</span>Edit</button>
                    <button type="button" (click)="openPrivilegeModal(user)"><span>⚿</span>Set Privilege</button>
                    <button type="button" (click)="regeneratePassword(user)"><span>↻</span>Regenerate Password</button>
                    <button type="button" class="danger-action" (click)="deleteUser(user)"><span>⌫</span>Delete User</button>
                  </div>
                }
              </td>
            </tr>
          }
        </table>
      </div>

      @if (filteredUsers().length === 0) {
        <div class="empty-state">No users found.</div>
      }

      @if (filteredUsers().length > 0) {
        <div class="pagination">
          <span>Showing {{ paginatedUsers().length }} of {{ filteredUsers().length }} users</span>
          <div>
            <button type="button" class="secondary-button" [disabled]="currentPage() === 1" (click)="goToPage(currentPage() - 1)">Previous</button>
            <strong>{{ currentPage() }} / {{ totalPages() }}</strong>
            <button type="button" class="secondary-button" [disabled]="currentPage() === totalPages()" (click)="goToPage(currentPage() + 1)">Next</button>
          </div>
        </div>
      }
    </div>
  }

  @if (isPrivilegeModalOpen()) {
    <div class="modal-backdrop" role="presentation">
      <div class="md-modal" role="dialog" aria-modal="true" aria-labelledby="privilege-modal-title">
        <div class="modal-header">
          <div>
            <p>Menu Rights</p>
            <h2 id="privilege-modal-title">Set Privilege</h2>
            <span>{{ privilegeUser()?.full_name }} · Read and write access by menu</span>
          </div>
          <button type="button" class="icon-button" (click)="closePrivilegeModal()" aria-label="Close privilege modal">×</button>
        </div>

        <div class="privilege-table-wrap">
          <table class="table-sm privilege-table">
            <tr>
              <th>Sr.No</th>
              <th>Menu</th>              
              <th>Read</th>
              <th>Write</th>
            </tr>
            @for (menu of privilegeRows(); track menu.id; let index = $index) {
              <tr [class.child-menu-row]="menu.level === 'Child'" [class.parent-group-row]="menu.hasChildren">
                <td>{{ index + 1 }}</td>
                <td>
                  <strong>{{ menu.name }}</strong>
                  @if (menu.parent_menu?.name) {
                    <small>Under {{ menu.parent_menu.name }}</small>
                  } @else if (menu.hasChildren) {
                    <small>Parent menu group</small>
                  }
                </td>
                <td>
                  @if (!menu.hasChildren) {
                    <button
                      type="button"
                      class="status-toggle permission-toggle"
                      [class.inactive]="!menu.can_read"
                      (click)="togglePrivilege(menu.id, 'read', !menu.can_read)"
                      [attr.aria-label]="menu.can_read ? 'Remove read permission' : 'Allow read permission'"
                    >
                      <span></span>
                    </button>
                  } @else {
                    <span class="permission-placeholder">-</span>
                  }
                </td>
                <td>
                  @if (!menu.hasChildren) {
                    <button
                      type="button"
                      class="status-toggle permission-toggle"
                      [class.inactive]="!menu.can_write"
                      (click)="togglePrivilege(menu.id, 'write', !menu.can_write)"
                      [attr.aria-label]="menu.can_write ? 'Remove write permission' : 'Allow write permission'"
                    >
                      <span></span>
                    </button>
                  } @else {
                    <span class="permission-placeholder">-</span>
                  }
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="7">No active menus found.</td>
              </tr>
            }
          </table>
        </div>

        <div class="modal-actions">
          <button type="button" (click)="savePrivileges()">Save Privilege</button>
          <button type="button" class="secondary-button" (click)="closePrivilegeModal()">Cancel</button>
        </div>
      </div>
    </div>
  }
</section>
